import { Button, Modal, ModalActions, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { TreeView } from "@kaltura/ds-react-tree";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { TruncatedLine } from "@kaltura/mediaspace-shared-ui";
import { useEffect, useMemo, useState } from "react";
import { StyledFolderIcon } from "../StyledComponents";
import { Controller, FieldError, useForm } from "react-hook-form";
import { EditFolderRequestParams, EditFolderResponseParams, Folder } from "../types";
import { TextField, FormLabel } from "@kaltura/mediaspace-shared-ui/form";
import { useFoldersStore } from "../foldersStore";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { StyledForm } from "../StyledComponents";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface EditFolderModalProps {
    /** boolean flag to indicate the modal is currently open */
    isOpen: boolean;
    /** callback to execute when clicking cancel */
    onClose: () => void;
    /** callback to execute after successful edit folder request (create/update) */
    onSubmitEnd?: () => void;
    /** current folder - initial data for update folder form */
    currentFolder?: Folder;
}

const ROOT_FOLDER_ID = "root";

/** root level -> level 1 -> level 2 */
export const MAX_NESTING_LEVEL = 3;

const baseClassName = "kms-ds-folders-edit-modal";

/**
 * edit folder modal
 * handles create/update folder form
 */
export function EditFolderModal({ isOpen, onClose, onSubmitEnd, currentFolder }: EditFolderModalProps) {
    const {
        register,
        handleSubmit,
        control,
        watch,
        trigger,
        setValue,
        formState: { errors, isValid, isDirty },
    } = useForm<EditFolderRequestParams>({
        mode: "onChange",
        defaultValues: {
            name: currentFolder?.name ?? "",
            parentId: currentFolder?.parentId ?? ROOT_FOLDER_ID,
        },
    });

    const [isProcessing, setIsProcessing] = useState(false);

    const folders = useFoldersStore((state) => state.folders);
    const foldersTreeItems = useFoldersStore((state) => state.foldersTreeItems);
    const listFolders = useFoldersStore((state) => state.listFolders);

    const sendButtonAnalytics = useButtonAnalytics();

    const allFolders = useMemo(
        () => [
            {
                id: ROOT_FOLDER_ID,
                label: <TruncatedLine>{translate("My folders")}</TruncatedLine>,
                subItems: foldersTreeItems,
            },
        ],
        [foldersTreeItems]
    );

    const { showToast } = useToastsContext();

    const selectedParentId = watch("parentId");

    // trigger name validation when parent is selected
    useEffect(() => {
        isDirty && trigger("name");
    }, [selectedParentId]);

    // selected parent's children names array
    // used for edit folder name validation
    const selectedParentChildrenNames = useMemo(() => {
        return folders
            .filter((folder) => {
                // renaming the current folder is allowed - skip
                if (folder.id === currentFolder?.id) {
                    return false;
                }
                // selected parent folder is the root folder
                if (selectedParentId === ROOT_FOLDER_ID) {
                    // return folders with no parent
                    return !folder.parentId;
                }
                // return direct children of the selected parent
                return selectedParentId === folder.parentId;
            })
            .map((folder) => folder.name.trim().toLowerCase());
    }, [folders, selectedParentId, currentFolder?.id]);

    const disabledIds = useMemo(() => {
        if (!folders.length) return [];

        const disabledSet = new Set<string>();
        let maxDepthFromCurrentFolder = 0;

        for (const folder of folders) {
            const { id, fullPathIds } = folder;

            // disable items by max nesting level
            if (fullPathIds.length >= MAX_NESTING_LEVEL) {
                disabledSet.add(id);
            }

            if (currentFolder) {
                // disable current folder - cannot move under itself
                if (currentFolder.id === id) {
                    disabledSet.add(currentFolder.id);
                }

                // calculate max depth from current folder
                const index = fullPathIds.indexOf(currentFolder.id);
                if (index !== -1) {
                    maxDepthFromCurrentFolder = Math.max(maxDepthFromCurrentFolder, fullPathIds.length - index);
                }
            }
        }

        // disable items by maxDepthFromCurrentFolder
        // if current folder has children, full path cannot exceed MAX_NESTING_LEVEL after move
        if (maxDepthFromCurrentFolder > 0) {
            for (const folder of folders) {
                folder.fullPathIds.forEach((folderId, index) => {
                    if (maxDepthFromCurrentFolder + index >= MAX_NESTING_LEVEL) {
                        disabledSet.add(folderId);
                    }
                });
            }
        }

        return Array.from(disabledSet);
    }, [folders, currentFolder]);

    const onSubmit = async (data: EditFolderRequestParams) => {
        setIsProcessing(true);
        const requestData: EditFolderRequestParams = currentFolder
            ? // update request
              {
                  id: currentFolder.id,
                  // pass empty name if it did not change
                  name: currentFolder.name === data.name ? "" : data.name,
                  // pass empty parentId if it did not change
                  parentId: (currentFolder?.parentId ?? ROOT_FOLDER_ID) === data.parentId ? "" : data.parentId,
              }
            : // create request
              data;
        try {
            const result: EditFolderResponseParams = await postKmsData(
                `${baseUrl}/folders/folders/${currentFolder ? "update" : "create"}`,
                requestData
            );
            setIsProcessing(false);

            showToast({
                severity: result.success ? "success" : "error",
                message: result.message,
            });

            if (result.success) {
                onSubmitEnd?.();
                // refresh store folders
                try {
                    await listFolders();
                }
                catch (e) {
                    showToast({
                        severity: "error",
                        message: translate("Failed to list updated folders"),
                    });
                }
            }
        }
        catch (e) {
            setIsProcessing(false);
            showToast({
                severity: "error",
                message: currentFolder
                    ? translate("Failed to update %1 folder", [currentFolder.name])
                    : translate("Failed to create %1 folder", [data.name]),
            });
        }
    };

    const handleClose = () => {
        sendButtonAnalytics(`${currentFolder ? "Update" : "Create"} folder - cancel`, ButtonClickAnalyticsType.CLOSE);
        onClose();
    };

    const sendSubmitButtonAnalytics = () => {
        sendButtonAnalytics(`${currentFolder ? "Update" : "Create"} folder - save`, ButtonClickAnalyticsType.SAVE);
    };

    return (
        <Modal
            classes={{
                root: `${baseClassName}-root`,
                paper: `${baseClassName}-paper`,
            }}
            open={isOpen}
            fullWidth={true}
        >
            <StyledForm onSubmit={handleSubmit(onSubmit)} className={`${baseClassName}-form`}>
                <ModalTitle className={`${baseClassName}-title`}>
                    {currentFolder ? translate("Update folder") : translate("Create folder")}
                </ModalTitle>
                <ModalContent className={`${baseClassName}-content`}>
                    <TextField
                        id={"name"}
                        title={translate("Name")}
                        required={true}
                        {...register(
                            "name",
                            {
                                required: translate("Folder name is required"),
                                validate: (value) => {
                                    const nameValue = value.trim().toLowerCase();
                                    if (selectedParentChildrenNames.includes(nameValue)) {
                                        return translate(
                                            "This folder name is already in use. Please choose a different name"
                                        );
                                    }
                                    return true;
                                },
                                onBlur: (e) => {
                                    // update value with trimmed string
                                    setValue("name", e.target.value.trim(), { shouldValidate: true });
                                },
                            }
                        )}
                        error={errors.name as FieldError}
                        errorMessage={errors.name?.message}
                    />
                    <FormLabel
                        htmlFor={"folderLocation"}
                        fontWeightBold={true}
                        text={translate("Choose folder location")}
                    />
                    <Controller
                        name="parentId"
                        control={control}
                        render={({ field }) => (
                            <TreeView
                                id="folderLocation"
                                items={allFolders}
                                defaultStartIcon={<StyledFolderIcon />}
                                iconExpansionOnly={true}
                                selectionComponentType={"radio"}
                                defaultExpandedItems={[
                                    ROOT_FOLDER_ID,
                                    ...(currentFolder ? currentFolder.fullPathIds.slice(0, -1) : []),
                                ]}
                                onSelectedItemsChange={(event, nodeId) => {
                                    field.onChange(nodeId);
                                }}
                                selectedItems={field.value}
                                disabledIds={disabledIds}
                            />
                        )}
                    />
                </ModalContent>
                <ModalActions className={`${baseClassName}-actions`}>
                    <Button
                        onClick={handleClose}
                        variant={"borderless"}
                        color={"secondary"}
                        className={`${baseClassName}-actions-cancel`}
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        type="submit"
                        loading={isProcessing}
                        disabled={isProcessing || !isValid || !isDirty}
                        className={`${baseClassName}-actions-submit`}
                        onClick={sendSubmitButtonAnalytics}
                    >
                        {currentFolder ? translate("Update") : translate("Create")}
                    </Button>
                </ModalActions>
            </StyledForm>
        </Modal>
    );
}

export default EditFolderModal;
